/**
 * SRES · useProjectJobsSocket — real-time push for narrative jobs +
 * paragraph approvals (Feb 2026 · Iter77).
 *
 * Replaces the 8-second polling loop in NarrativeJobsPanel with a
 * WebSocket subscription. Falls back to polling automatically if the
 * socket fails to connect (reconnect attempts x 3 → give up).
 *
 * Usage
 * -----
 * ```
 * const { status } = useProjectJobsSocket(projectId, {
 *   onMessage: (msg) => { … },
 * });
 * // status ∈ "connecting" | "open" | "closed" | "polling-fallback"
 * ```
 *
 * The hook takes care of:
 *   - auto-reconnect with exponential backoff (1s, 2s, 4s, 8s, cap 30s)
 *   - JWT token from localStorage (`sres_token`) attached via ?token=
 *   - keep-alive ping every 25s
 *   - invalidating the shared React-Query cache for job progress on
 *     every incoming ``job_*`` or ``paragraph_*`` message (caller can
 *     override via ``onMessage`` if needed)
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { JOBS_QUERY_KEY } from "@/pages/wizard/NarrativeJobsPanel";

const MAX_RECONNECT_ATTEMPTS = 5;
const BACKOFF_CAP_MS = 30_000;

function buildWsUrl(projectId, token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return null;
  const scheme = base.startsWith("https") ? "wss" : "ws";
  const stripped = base.replace(/^https?:\/\//, "");
  const q = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${scheme}://${stripped}/api/ws/projects/${projectId}/jobs${q}`;
}

export default function useProjectJobsSocket(projectId, {
  onMessage,
  invalidateCacheKeys = [],
} = {}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("idle");
  const [lastEvent, setLastEvent] = useState(null);
  const socketRef = useRef(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  // Keep the latest callback + cache-key list in a ref so the effect
  // never re-fires just because the caller re-rendered with a new
  // inline array / lambda. Without this the socket would close and
  // re-open on every parent render, which meant `status` was stuck
  // on "connecting" indefinitely.
  const onMessageRef = useRef(onMessage);
  const cacheKeysRef = useRef(invalidateCacheKeys);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { cacheKeysRef.current = invalidateCacheKeys; },
              [invalidateCacheKeys]);

  useEffect(() => {
    if (!projectId) return;
    stoppedRef.current = false;

    const token = localStorage.getItem("sres_token")
                    || sessionStorage.getItem("sres_token")
                    || localStorage.getItem("of_token")
                    || sessionStorage.getItem("of_token");
    if (!token) {
      setStatus("polling-fallback");
      return;
    }

    const openSocket = () => {
      const url = buildWsUrl(projectId, token);
      if (!url) return;
      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        setStatus("polling-fallback");
        return;
      }
      socketRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus("open");
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          setLastEvent(msg);
          if (msg.type && (
            msg.type.startsWith("job_")
              || msg.type.startsWith("paragraph_")
              || msg.type === "bulk_approve_done"
          )) {
            qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY(projectId) });
            for (const k of (cacheKeysRef.current || [])) {
              qc.invalidateQueries({ queryKey: k });
            }
          }
          if (onMessageRef.current) onMessageRef.current(msg);
        } catch (e) {
          // ignore malformed frames
        }
      };
      ws.onerror = () => {
        // Actual failure surfaces via onclose.
      };
      ws.onclose = () => {
        socketRef.current = null;
        if (stoppedRef.current) return;
        attemptRef.current += 1;
        if (attemptRef.current > MAX_RECONNECT_ATTEMPTS) {
          setStatus("polling-fallback");
          return;
        }
        const backoff = Math.min(BACKOFF_CAP_MS,
                                 1000 * (2 ** (attemptRef.current - 1)));
        setStatus("closed");
        reconnectTimerRef.current = setTimeout(openSocket, backoff);
      };
    };

    openSocket();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (socketRef.current) {
        try { socketRef.current.close(); } catch (e) { /* noop */ }
        socketRef.current = null;
      }
    };
    // The socket is bound to the projectId only — callbacks and
    // cache-key arrays flow through refs (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, qc]);

  return { status, lastEvent };
}
